import React, { useContext } from "react";
import parse, { domToReact } from "html-react-parser";
import PropTypes from "prop-types";
import classnames from "classnames";

import DetailsRow from "./DetailsRow";
import WidgetSettingsContext from "../../../../contexts/WidgetSettingsContext";
import YouTubePreview from "./YoutubePreview";
import VimeoPreview from "./VimeoPreview";
import { useTranslation } from "react-i18next";

const hasYouTubeLink = (str) => /youtube.com|youtu.be/.test(str);
const hasVimeoLink = (str) => /vimeo.com/.test(str);

const unwrapGoogleRedirect = (url) => {
  try {
    const parsed = new URL(url);

    // Handle Google redirect links
    if (parsed.hostname === "www.google.com" && parsed.pathname === "/url") {
      const realUrl = parsed.searchParams.get("q");
      return realUrl ? decodeURIComponent(realUrl) : url;
    }

    return url;
  } catch {
    return url;
  }
};

/**
 * Normalizes a URL so that cosmetically-different but equivalent links
 * compare as equal. Deliberately mirrors how the backend derives CTA URLs
 * in getLinkFromAnchor (widgetItems.js), which reads the DOM's resolved
 * `aTag.href` rather than the raw attribute - that resolution silently
 * adds a trailing slash to bare origins, lowercases scheme/host, etc.
 * Using `new URL(...).href` here applies the same canonicalization so a
 * description link and its matching CTA URL compare equal even when their
 * raw attribute strings differ cosmetically.
 *
 * @param {string} url
 * @returns {string}
 */
const normalizeUrlForComparison = (url) => {
  if (!url) return "";

  const unwrapped = unwrapGoogleRedirect(url);

  try {
    return new URL(unwrapped).href;
  } catch {
    // Relative or otherwise unparseable - fall back to a plain trim so we
    // still get *some* comparison rather than throwing.
    return unwrapped.trim();
  }
};

/**
 * Whether a description link points to the same destination as one of the
 * URLs we want to hide (typically because it's already shown as a CTA
 * button elsewhere on the card/detail view).
 *
 * @param {string} href
 * @param {string[]} duplicateLinkUrls
 * @returns {boolean}
 */
const isDuplicateCtaLink = (href, duplicateLinkUrls) => {
  if (!href || !duplicateLinkUrls || !duplicateLinkUrls.length) return false;

  const normalizedHref = normalizeUrlForComparison(href);
  if (!normalizedHref) return false;

  return duplicateLinkUrls.some(
    (url) => normalizeUrlForComparison(url) === normalizedHref
  );
};

const renderVideoPreview = (rawUrl) => {
  const url = unwrapGoogleRedirect(rawUrl);

  if (hasYouTubeLink(url)) {
    const videoMatch = url.match(
      /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/
    );
    const videoId = videoMatch?.[1];
    return videoId ? <YouTubePreview videoId={videoId} /> : null;
  } else if (hasVimeoLink(url)) {
    const videoId = url.match(/(?:vimeo\.com\/|video\/)(\d+)/)?.[1];
    return videoId ? <VimeoPreview videoId={videoId} /> : null;
  }
  return null;
};

const getParseOptions = (
  turnVideoLinkIntoPreview,
  openLinksInNewTab,
  underlineLinks,
  onVideoFound,
  hideDuplicateLinks,
  duplicateLinkUrls
) => ({
  replace: (domNode) => {
    const { type, name, attribs, children, data } = domNode;

    if (turnVideoLinkIntoPreview) {
      if (type === "tag" && name === "a" && attribs?.href) {
        const url = attribs.href;
        const VideoPreviewComponent = renderVideoPreview(url);

        if (!VideoPreviewComponent) {
          // Only hide this link if it's NOT becoming a video preview -
          // a CTA that happens to point at a video should still show the
          // embed, even though the plain "Book table"-style link case
          // below is exactly what this setting is meant to declutter.
          if (
            hideDuplicateLinks &&
            isDuplicateCtaLink(url, duplicateLinkUrls)
          ) {
            return <></>;
          }

          const newAttribs = {
            ...attribs,
            target: openLinksInNewTab ? "_blank" : "_self",
            style: underlineLinks
              ? { textDecoration: "underline" }
              : attribs.style,
          };
          return <a {...newAttribs}>{domToReact(children)}</a>;
        }

        onVideoFound();
        return VideoPreviewComponent;
      } else if (type === "text" && data) {
        const VideoPreviewComponent = renderVideoPreview(data);
        if (VideoPreviewComponent) {
          onVideoFound();
        }
        return VideoPreviewComponent || null;
      }
    } else {
      if (type === "tag" && name === "a") {
        if (
          hideDuplicateLinks &&
          attribs?.href &&
          isDuplicateCtaLink(attribs.href, duplicateLinkUrls)
        ) {
          return <></>;
        }

        const newAttribs = {
          ...attribs,
          target: openLinksInNewTab ? "_blank" : "_self",
          style: underlineLinks
            ? { textDecoration: "underline" }
            : attribs.style,
        };
        return <a {...newAttribs}>{domToReact(children)}</a>;
      }
    }
  },
});

const EventDescription = React.memo(
  ({ event, hideDuplicateLinks = false, duplicateLinkUrls = [] }) => {
    const { t } = useTranslation();
    const settingsContext = useContext(WidgetSettingsContext);

    const openLinksInNewTab = Number(settingsContext["openLinksNewTab"]) === 1;
    const underlineLinks =
      Number(settingsContext["generalUnderlineLinks"]) === 1;
    const turnVideoLinkIntoPreview =
      Number(settingsContext["turnVideoLinkIntoPreview"]) === 1;

    let hasVideoPreview = false;

    const parsedDescription = event.description
      ? parse(
          event.description,
          getParseOptions(
            turnVideoLinkIntoPreview,
            openLinksInNewTab,
            underlineLinks,
            () => {
              hasVideoPreview = true;
            },
            hideDuplicateLinks,
            duplicateLinkUrls
          )
        )
      : "";

    return (
      <DetailsRow title={t("Event details")} icon="reorder">
        <div
          className={classnames("event-row--details__pre", {
            "event-row--details__pre--has-video": hasVideoPreview,
          })}
        >
          {parsedDescription}
        </div>
      </DetailsRow>
    );
  }
);

EventDescription.propTypes = {
  event: PropTypes.object.isRequired,
  hideDuplicateLinks: PropTypes.bool,
  duplicateLinkUrls: PropTypes.arrayOf(PropTypes.string),
};

export default EventDescription;
